import { DialogComponent } from '@theme/dialog';

class CustomDrawer extends DialogComponent {
  open() {
    this.showDialog();
  }

  async close() {
    await this.closeDialog();
  }
}

if (!customElements.get('custom-drawer')) {
  customElements.define('custom-drawer', CustomDrawer);
}

/**
 * Swiper-based slider custom element with full Swiper.js functionality.
 *
 * Usage example:
 * <swipe-slider
 *   data-autoplay="true"
 *   data-delay="4000"
 *   data-loop="true"
 *   data-speed="300"
 *   data-effect="slide"
 *   data-slides-per-view="1"
 *   data-space-between="0"
 *   data-navigation="true"
 *   data-pagination="true"
 *   data-pagination-type="bullets"
 *   data-scrollbar="false"
 *   data-direction="horizontal"
 *   data-free-mode="false"
 *   data-grab-cursor="true"
 *   data-keyboard="true"
 *   data-mousewheel="false"
 *   data-lazy="true"
 *   data-centered-slides="false"
 * >
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">Slide 1</div>
 *       <div class="swiper-slide">Slide 2</div>
 *       <div class="swiper-slide">Slide 3</div>
 *     </div>
 *     <!-- Optional: Add Pagination -->
 *     <div class="swiper-pagination"></div>
 *     <!-- Optional: Add Navigation -->
 *     <div class="swiper-button-prev"></div>
 *     <div class="swiper-button-next"></div>
 *     <!-- Optional: Add Scrollbar -->
 *     <div class="swiper-scrollbar"></div>
 *   </div>
 * </swipe-slider>
 *
 * Responsive Breakpoints (individual data attributes):
 * Use these for simple responsive control:
 *   data-slides-sm="2"    data-space-sm="10"   // 480px+
 *   data-slides-md="3"    data-space-md="20"   // 768px+
 *   data-slides-lg="4"    data-space-lg="30"   // 1024px+
 *   data-slides-xl="5"    data-space-xl="40"   // 1280px+
 *   data-slides-2xl="6"   data-space-2xl="50"  // 1536px+
 *
 * Or use JSON for advanced control:
 *   data-breakpoints='{"640":{"slidesPerView":2},"1024":{"slidesPerView":3}}'
 */
class SwipeSlider extends HTMLElement {
  /** @type {any} */
  #swiperInstance = null;

  connectedCallback() {
    // Wait for Swiper to be available
    if (typeof Swiper === 'undefined') {
      console.error('Swiper library is not loaded');
      return;
    }

    this.#initSwiper();
  }

  disconnectedCallback() {
    if (this.#swiperInstance) {
      this.#swiperInstance.destroy(true, true);
      this.#swiperInstance = null;
    }
  }

  #initSwiper() {
    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer) {
      console.error('Swiper container (.swiper) not found');
      return;
    }

    // Build Swiper configuration from data attributes
    const config = this.#buildConfig();

    // Initialize Swiper
    this.#swiperInstance = new Swiper(swiperContainer, config);

    // Expose instance for external access
    this.swiper = this.#swiperInstance;
  }

  #buildConfig() {
    const config = {
      // Core parameters
      direction: this.dataset.direction || 'horizontal',
      loop: this.dataset.loop !== 'false',
      speed: parseInt(this.dataset.speed || '300', 10),
      effect: this.dataset.effect || 'slide',
      slidesPerView: this.#parseNumberOrString(this.dataset.slidesPerView || '1'),
      spaceBetween: parseInt(this.dataset.spaceBetween || '30', 10),
      centeredSlides: this.dataset.centeredSlides === 'true',
      grabCursor: this.dataset.grabCursor !== 'false',

      // Free mode
      freeMode: this.dataset.freeMode === 'true',

      // Keyboard control
      keyboard: this.dataset.keyboard === 'true' ? {
        enabled: true,
        onlyInViewport: true,
      } : false,

      // Mousewheel control
      mousewheel: this.dataset.mousewheel === 'true' ? {
        forceToAxis: true,
      } : false,

      // Lazy loading
      lazy: this.dataset.lazy === 'true' ? {
        loadPrevNext: true,
      } : false,
    };

    // Autoplay
    if (this.dataset.autoplay === 'true') {
      config.autoplay = {
        delay: parseInt(this.dataset.delay || '4000', 10),
        disableOnInteraction: this.dataset.disableOnInteraction !== 'false',
        pauseOnMouseEnter: this.dataset.pauseOnMouseEnter === 'true',
      };
    }

    // Navigation
    if (this.dataset.navigation === 'true') {
      const prevEl = this.querySelector('.swiper-button-prev');
      const nextEl = this.querySelector('.swiper-button-next');
      if (prevEl && nextEl) {
        config.navigation = {
          nextEl: nextEl,
          prevEl: prevEl,
        };
      }
    }

    // Pagination
    if (this.dataset.pagination === 'true') {
      const paginationEl = this.querySelector('.swiper-pagination');
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          type: this.dataset.paginationType || 'bullets',
          clickable: true,
          dynamicBullets: this.dataset.dynamicBullets === 'true',
        };
      }
    }

    // Scrollbar
    if (this.dataset.scrollbar === 'true') {
      const scrollbarEl = this.querySelector('.swiper-scrollbar');
      if (scrollbarEl) {
        config.scrollbar = {
          el: scrollbarEl,
          draggable: true,
        };
      }
    }

    // Breakpoints - Support both JSON and individual data attributes
    // Individual data attributes take precedence over JSON
    /** @type {any} */
    const breakpoints = {};

    // Check for individual breakpoint data attributes
    // Mobile (xs): 0-479px - use base slidesPerView
    // Small (sm): 480-767px
    if (this.dataset.slidesSm || this.dataset.spaceSm) {
      breakpoints[480] = {};
      if (this.dataset.slidesSm) {
        breakpoints[480].slidesPerView = this.#parseNumberOrString(this.dataset.slidesSm);
      }
      if (this.dataset.spaceSm) {
        breakpoints[480].spaceBetween = parseInt(this.dataset.spaceSm, 10);
      }
    }

    // Medium (md): 768-1023px
    if (this.dataset.slidesMd || this.dataset.spaceMd) {
      breakpoints[768] = {};
      if (this.dataset.slidesMd) {
        breakpoints[768].slidesPerView = this.#parseNumberOrString(this.dataset.slidesMd);
      }
      if (this.dataset.spaceMd) {
        breakpoints[768].spaceBetween = parseInt(this.dataset.spaceMd, 10);
      }
    }

    // Large (lg): 1024-1279px
    if (this.dataset.slidesLg || this.dataset.spaceLg) {
      breakpoints[1024] = {};
      if (this.dataset.slidesLg) {
        breakpoints[1024].slidesPerView = this.#parseNumberOrString(this.dataset.slidesLg);
      }
      if (this.dataset.spaceLg) {
        breakpoints[1024].spaceBetween = parseInt(this.dataset.spaceLg, 10);
      }
    }

    // Extra Large (xl): 1280-1535px
    if (this.dataset.slidesXl || this.dataset.spaceXl) {
      breakpoints[1280] = {};
      if (this.dataset.slidesXl) {
        breakpoints[1280].slidesPerView = this.#parseNumberOrString(this.dataset.slidesXl);
      }
      if (this.dataset.spaceXl) {
        breakpoints[1280].spaceBetween = parseInt(this.dataset.spaceXl, 10);
      }
    }

    // 2XL: 1536px+
    if (this.dataset.slides2xl || this.dataset.space2xl) {
      breakpoints[1536] = {};
      if (this.dataset.slides2xl) {
        breakpoints[1536].slidesPerView = this.#parseNumberOrString(this.dataset.slides2xl);
      }
      if (this.dataset.space2xl) {
        breakpoints[1536].spaceBetween = parseInt(this.dataset.space2xl, 10);
      }
    }

    // If individual breakpoints were set, use them
    if (Object.keys(breakpoints).length > 0) {
      /** @type {any} */ (config).breakpoints = breakpoints;
    }
    // Otherwise, fall back to JSON breakpoints if provided
    else if (this.dataset.breakpoints) {
      try {
        /** @type {any} */ (config).breakpoints = JSON.parse(this.dataset.breakpoints);
      } catch (e) {
        console.error('Invalid breakpoints JSON:', e);
      }
    }

    // Effect-specific options
    if (config.effect === 'fade') {
      /** @type {any} */ (config).fadeEffect = {
        crossFade: true,
      };
    } else if (config.effect === 'cube') {
      /** @type {any} */ (config).cubeEffect = {
        shadow: this.dataset.cubeShadow !== 'false',
        slideShadows: this.dataset.cubeSlideShadows !== 'false',
        shadowOffset: parseInt(this.dataset.cubeShadowOffset || '20', 10),
        shadowScale: parseFloat(this.dataset.cubeShadowScale || '0.94'),
      };
    } else if (config.effect === 'flip') {
      /** @type {any} */ (config).flipEffect = {
        slideShadows: this.dataset.flipSlideShadows !== 'false',
        limitRotation: this.dataset.flipLimitRotation !== 'false',
      };
    } else if (config.effect === 'coverflow') {
      /** @type {any} */ (config).coverflowEffect = {
        rotate: parseInt(this.dataset.coverflowRotate || '50', 10),
        stretch: parseInt(this.dataset.coverflowStretch || '0', 10),
        depth: parseInt(this.dataset.coverflowDepth || '100', 10),
        modifier: parseInt(this.dataset.coverflowModifier || '1', 10),
        slideShadows: this.dataset.coverflowSlideShadows !== 'false',
      };
    } else if (config.effect === 'creative') {
      if (this.dataset.creativeEffect) {
        try {
          /** @type {any} */ (config).creativeEffect = JSON.parse(this.dataset.creativeEffect);
        } catch (e) {
          console.error('Invalid creative effect JSON:', e);
        }
      }
    } else if (config.effect === 'cards') {
      /** @type {any} */ (config).cardsEffect = {
        slideShadows: this.dataset.cardsSlideShadows !== 'false',
        perSlideOffset: parseInt(this.dataset.cardsPerSlideOffset || '8', 10),
        perSlideRotate: parseInt(this.dataset.cardsPerSlideRotate || '2', 10),
      };
    }

    // Grid (for multi-row layouts)
    if (this.dataset.gridRows) {
      config.grid = {
        rows: parseInt(this.dataset.gridRows, 10),
        fill: this.dataset.gridFill || 'row',
      };
    }

    // Thumbs (for thumbnail navigation)
    if (this.dataset.thumbsSwiper) {
      const thumbsElement = document.querySelector(this.dataset.thumbsSwiper);
      if (thumbsElement && thumbsElement.swiper) {
        config.thumbs = {
          swiper: thumbsElement.swiper,
        };
      }
    }

    // Virtual slides
    if (this.dataset.virtual === 'true') {
      config.virtual = {
        enabled: true,
      };
    }

    // Hash navigation
    if (this.dataset.hashNavigation === 'true') {
      config.hashNavigation = {
        watchState: true,
      };
    }

    // History navigation
    if (this.dataset.history === 'true') {
      config.history = {
        key: this.dataset.historyKey || 'slide',
      };
    }

    return config;
  }

  /**
   * Parse a value that could be a number or string like 'auto'
   * @param {string} value
   * @returns {number | string}
   */
  #parseNumberOrString(value) {
    if (value === 'auto') return 'auto';
    const num = parseFloat(value);
    return isNaN(num) ? value : num;
  }

  // Public API methods
  next() {
    if (this.#swiperInstance) {
      this.#swiperInstance.slideNext();
    }
  }

  prev() {
    if (this.#swiperInstance) {
      this.#swiperInstance.slidePrev();
    }
  }

  /** @param {number} index */
  goTo(index) {
    if (this.#swiperInstance) {
      this.#swiperInstance.slideTo(index);
    }
  }

  update() {
    if (this.#swiperInstance) {
      this.#swiperInstance.update();
    }
  }

  destroy() {
    if (this.#swiperInstance) {
      this.#swiperInstance.destroy(true, true);
      this.#swiperInstance = null;
    }
  }
}

if (!customElements.get('swipe-slider')) {
  customElements.define('swipe-slider', SwipeSlider);
}


